import { useCallback, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { PanelSnapshot } from '../lib/panelSnapshot'

/**
 * Hook do chat IA do BX (aba Miguel). Conversa multi-turno sobre o painel,
 * com memória (regras que o usuário ensina) e feedback (👍/👎). A Edge
 * Function `ai-chat` recebe o histórico + um snapshot AGREGADO do painel
 * (nunca linha bruta) e devolve a resposta, persistindo as mensagens.
 */

export type ChatRole = 'user' | 'assistant'
export interface ChatMessage {
  /** id da linha em ai_chat_messages — presente em mensagens persistidas (necessário p/ feedback). */
  id?: string
  role: ChatRole
  content: string
}

export interface MemoryItem { id: string; content: string }

interface SendResult { ok: boolean; text?: string; message_id?: string | null; error?: string }

interface InvokeResult { ok: boolean; text?: string; message_id?: string | null; error?: string }

async function currentCompanyId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return null
  const { data: profile } = await supabase.from('profiles').select('company_id').eq('id', user.id).single()
  return (profile?.company_id as string | undefined) ?? null
}

export function useAiChat() {
  const [sending, setSending] = useState(false)

  // ── Conversa ─────────────────────────────────────────────────────────────
  const send = useCallback(
    async (messages: ChatMessage[], panel: PanelSnapshot): Promise<SendResult> => {
      setSending(true)
      try {
        const payload = { messages: messages.map(m => ({ role: m.role, content: m.content })), panel }
        const { data, error } = await supabase.functions.invoke<InvokeResult>('ai-chat', { body: payload })
        if (data?.ok && data.text) return { ok: true, text: data.text, message_id: data.message_id ?? null }
        if (data?.error) return { ok: false, error: data.error }

        // supabase-js levanta FunctionsHttpError em 4xx/5xx; tenta extrair o body real.
        interface SbErrCtx { context?: { json?: () => Promise<unknown>; response?: Response; status?: number } }
        const ctx = (error as SbErrCtx | undefined)?.context
        if (ctx?.json) {
          try {
            const b = await ctx.json() as { error?: string } | undefined
            if (b?.error) return { ok: false, error: b.error }
          } catch { /* noop */ }
        }
        if (ctx?.response) {
          try {
            const b = await ctx.response.clone().json() as { error?: string } | undefined
            if (b?.error) return { ok: false, error: b.error }
          } catch { /* noop */ }
        }
        // eslint-disable-next-line no-console
        console.error('[useAiChat] invoke falhou', { error, ctx })
        return { ok: false, error: error?.message?.trim() || 'Falha ao chamar a IA — veja o console (F12).' }
      } finally {
        setSending(false)
      }
    },
    [],
  )

  const loadHistory = useCallback(async (limit = 30): Promise<ChatMessage[]> => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return []
    const { data, error } = await supabase
      .from('ai_chat_messages')
      .select('id, role, content')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(limit)
    if (error || !data) return []
    // Veio em ordem decrescente — inverte para exibir cronológico.
    return data.reverse().map(r => ({ id: r.id as string, role: r.role as ChatRole, content: r.content as string }))
  }, [])

  const clearHistory = useCallback(async (): Promise<boolean> => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return false
    // Apaga só a conversa do próprio usuário; o feedback some junto (FK cascade).
    // A memória (regras) fica intacta.
    const { error } = await supabase.from('ai_chat_messages').delete().eq('user_id', user.id)
    return !error
  }, [])

  const saveFeedback = useCallback(async (messageId: string, rating: 'up' | 'down', note?: string): Promise<boolean> => {
    const companyId = await currentCompanyId()
    const { data: { user } } = await supabase.auth.getUser()
    if (!companyId || !user) return false
    const { error } = await supabase.from('ai_chat_feedback').insert({
      company_id: companyId, user_id: user.id, message_id: messageId, rating, note: note ?? null,
    })
    return !error
  }, [])

  // ── Memória ──────────────────────────────────────────────────────────────
  const listMemory = useCallback(async (): Promise<MemoryItem[]> => {
    const { data, error } = await supabase
      .from('ai_company_memory')
      .select('id, content')
      .order('created_at', { ascending: false })
    if (error || !data) return []
    return data.map(r => ({ id: r.id as string, content: r.content as string }))
  }, [])

  const addMemory = useCallback(async (content: string): Promise<boolean> => {
    const trimmed = content.trim()
    if (!trimmed) return false
    const companyId = await currentCompanyId()
    const { data: { user } } = await supabase.auth.getUser()
    if (!companyId) return false
    const { error } = await supabase.from('ai_company_memory').insert({
      company_id: companyId, content: trimmed, created_by: user?.id ?? null,
    })
    return !error
  }, [])

  const deleteMemory = useCallback(async (id: string): Promise<boolean> => {
    const { error } = await supabase.from('ai_company_memory').delete().eq('id', id)
    return !error
  }, [])

  return { sending, send, loadHistory, clearHistory, saveFeedback, listMemory, addMemory, deleteMemory }
}
